import { useMemo } from 'react';

import { format } from 'date-fns';

import { EmbryoLink } from '@/components/common/links';
import { SemenLink } from '@/components/common/links/SemenLink';
import { CellRendererParams, ColDef } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { ShipmentEmbryo, ShipmentSemen } from '@/services/shipping/types';

import { CaneCellRenderer } from '../components/CaneCellRenderer';
import { getCaneHighlightColor } from './cane-highlight-colors';
import { colorMap } from './constants';

export function useEmbryoColumnDefs(
  recaneEmbryoIds?: string[],
  allEmbryos?: ShipmentEmbryo[] | null,
  multipleCanesEmbryoIds?: string[],
): ColDef<ShipmentEmbryo>[] {
  const isRecanedIndicatorEnabled = useFeatureFlag(
    'Shipping_Recaned_Indicator',
  );
  const isStorageLocationSiteEnabled = useFeatureFlag(
    'shipping_storage_location_site',
  );

  return useMemo(
    () =>
      [
        {
          headerName: 'Name',
          field: 'name',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          suppressMovable: true,
          lockPosition: true,
          cellRenderer: EmbryoLink,
          cellRendererParams: (params: CellRendererParams<ShipmentEmbryo>) => ({
            embryoId: params.data?.id as string,
            embryoName: params.data?.name as string,
          }),
        },
        {
          headerName: 'Owner',
          field: 'owner.code',
        },
        {
          headerName: 'Storage Location',
          field: 'storageLocation.name',
        },
        ...(isStorageLocationSiteEnabled
          ? ([
              {
                headerName: 'Storage Location Site',
                field: 'storageLocationSite.name',
              },
            ] as ColDef<ShipmentEmbryo>[])
          : []),
        {
          headerName: 'Cane',
          field: 'cane',
          cellRenderer: CaneCellRenderer,
          cellRendererParams: {
            recaneEmbryoIds,
            allEmbryos: allEmbryos || [],
          },
          cellStyle: (params) => {
            if (!params || !params.data) {
              return undefined;
            }

            const color = getCaneHighlightColor(
              params.data,
              allEmbryos || [],
              multipleCanesEmbryoIds,
            );
            if (color) {
              const backgroundColor = colorMap[color];
              if (backgroundColor) {
                return { backgroundColor };
              }
            }
            return undefined;
          },
        },
        ...(isRecanedIndicatorEnabled
          ? ([
              {
                headerName: 'Recaned',
                field: 'recaned',
                valueGetter: (params) => params.data?.recaned === true,
                cellDataType: 'boolean',
                width: 90,
                minWidth: 90,
              },
            ] as ColDef<ShipmentEmbryo>[])
          : []),
        {
          headerName: 'Embryo Status',
          field: 'statusValue',
        },
        {
          headerName: 'Straw',
          field: 'strawNumber',
        },
        {
          headerName: 'Stage',
          field: 'stage',
        },
        {
          headerName: 'Method',
          field: 'methodValue',
        },
        {
          headerName: 'Embryo Creation',
          field: 'creation',
          valueFormatter: (params) => {
            return params.value ? format(params.value, 'MM-dd-yyyy') : '';
          },
        },
        {
          headerName: 'Sex',
          field: 'sex',
          valueGetter: ({ data }) => {
            switch (data?.sex) {
              case 100000000:
                return 'Female';
              case 100000001:
                return 'Male';
              case 100000002:
                return 'Unknown';
              default:
                return 'N/A';
            }
          },
        },
      ] as ColDef<ShipmentEmbryo>[],
    [
      recaneEmbryoIds,
      allEmbryos,
      multipleCanesEmbryoIds,
      isRecanedIndicatorEnabled,
      isStorageLocationSiteEnabled,
    ],
  );
}

export function useSemenColumnDefs(
  enableSelection = false,
): ColDef<ShipmentSemen>[] {
  const isStorageLocationSiteEnabled = useFeatureFlag(
    'shipping_storage_location_site',
  );

  return useMemo(
    () =>
      [
        ...(enableSelection
          ? [
              {
                checkboxSelection: true,
                headerCheckboxSelection: true,
                maxWidth: 48,
                pinned: 'left' as const,
                suppressMenu: true,
                sortable: false,
                filter: false,
                resizable: false,
              },
            ]
          : []),
        {
          field: 'name',
          filter: 'agTextColumnFilter',
          initialSort: 'asc',
          cellRenderer: SemenLink,
          cellRendererParams: (params: CellRendererParams<ShipmentEmbryo>) => ({
            semenId: params.data?.id as string,
            semenName: params.data?.name as string,
          }),
        },
        { field: 'cane', filter: 'agTextColumnFilter' },
        {
          headerName: 'TOG Cane',
          field: 'togCane',
          filter: 'agTextColumnFilter',
        },
        { field: 'quantity', filter: 'agNumberColumnFilter' },
        {
          headerName: 'Quantity to Ship',
          field: 'quantityToShip',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Storage Location',
          field: 'storageLocation.name',
          filter: 'agTextColumnFilter',
        },
        ...(isStorageLocationSiteEnabled
          ? [
              {
                headerName: 'Storage Location Site',
                field: 'storageLocationSite.name',
                filter: 'agTextColumnFilter',
              },
            ]
          : []),
        {
          headerName: 'Sexed',
          field: 'sexed',
        },
        {
          headerName: 'Sex',
          field: 'sex',
          valueGetter: ({ data }) => data?.sexValue,
          filter: 'agSetColumnFilter',
          filterParams: { defaultToNothingSelected: true },
        },
        { field: 'concentration', filter: 'agNumberColumnFilter' },
        { field: 'cost', filter: 'agNumberColumnFilter' },
        {
          field: 'dateReceived',
          filter: 'agDateColumnFilter',
        },
        { field: 'colCode', filter: 'agTextColumnFilter' },
        { field: 'soldTo', filter: 'agTextColumnFilter' },
        {
          headerName: 'Ranch',
          field: 'owner.code',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Sire',
          field: 'sire.name',
          filter: 'agTextColumnFilter',
        },
      ] as ColDef<ShipmentSemen>[],
    [enableSelection, isStorageLocationSiteEnabled],
  );
}

export const donorColumnDefs: ColDef<any>[] = [
  {
    headerName: 'Donor Name (Tag)',
    field: 'name',
    filter: 'agTextColumnFilter',
    initialSort: 'asc',
  },
  {
    headerName: 'Breed',
    field: 'breed.name',
    filter: 'agTextColumnFilter',
  },
  {
    field: 'registrationNumber',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Ear Tag',
    field: 'tattoo',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Metal/RFID Tag',
    field: 'metalTag',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Ranch ID (Owner)',
    field: 'owner.code',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Owner',
    field: 'owner.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Received',
    field: 'received',
    filter: 'agDateColumnFilter',
  },
  {
    headerName: 'Pregnant',
    field: 'pregnant',
    valueGetter: ({ data }) => data?.pregnantValue,
    filter: 'agSetColumnFilter',
    filterParams: { defaultToNothingSelected: true },
  },
  {
    headerName: 'Last Bred',
    field: 'lastBred',
    filter: 'agDateColumnFilter',
  },
  {
    headerName: 'Yard',
    field: 'yard.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Ranch Tag',
    field: 'ranchTag',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Ship Date',
    field: 'ship',
    filter: 'agDateColumnFilter',
  },
  {
    field: 'lastActivity',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Activity Date',
    field: 'activityDate',
    filter: 'agDateColumnFilter',
  },
  {
    headerName: 'Created On',
    field: 'createdOn',
    filter: 'agDateColumnFilter',
  },
  {
    headerName: 'Birth Date',
    field: 'birth',
    filter: 'agDateColumnFilter',
  },
];

export const recipientColumnDefs: ColDef<any>[] = [
  { field: 'tag', filter: 'agTextColumnFilter', initialSort: 'asc' },
  {
    headerName: 'Received',
    field: 'received',
    filter: 'agDateColumnFilter',
    initialSort: 'desc',
  },
  {
    headerName: 'Owner',
    field: 'owner.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Yard (Yard)',
    field: 'yard.yard',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Yard',
    field: 'yard.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Recipient Status',
    field: 'recipientStatus.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Last Activity Date',
    field: 'activityDate',
    filter: 'agDateColumnFilter',
  },
  {
    field: 'lastActivity',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Metal/RFID Tag',
    field: 'metalTag',
    filter: 'agTextColumnFilter',
  },
];

export const calvesColumnDefs: ColDef<any>[] = [
  { field: 'tag', filter: 'agTextColumnFilter', initialSort: 'asc' },
  {
    headerName: 'Ranch ID (Owner)',
    field: 'owner.code',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Owner',
    field: 'owner.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Donor',
    field: 'donor.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Recipient',
    field: 'recipient.tag',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Calf Status',
    field: 'status.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Yard',
    field: 'yard.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Pen',
    field: 'pen.name',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Method',
    field: 'method',
    valueGetter: ({ data }) => data?.methodValue,
    filter: 'agSetColumnFilter',
    filterParams: { defaultToNothingSelected: true },
  },
  {
    field: 'tattoo',
    filter: 'agTextColumnFilter',
  },
  {
    headerName: 'Sex',
    field: 'sex',
    valueGetter: ({ data }) => data?.sexValue,
    filter: 'agSetColumnFilter',
    filterParams: { defaultToNothingSelected: true },
  },
  {
    headerName: 'Birth Date',
    field: 'birth',
    filter: 'agDateColumnFilter',
  },
  {
    headerName: 'Ship/Death Date',
    field: 'ship',
    filter: 'agDateColumnFilter',
  },
  {
    headerName: 'Created On',
    field: 'createdOn',
    filter: 'agDateColumnFilter',
  },
];
